import React from "react";
import { useIntl } from "react-intl";

import {
	ab0Messages,
	rhMessages
} from "lib/build/forms/ktx/basic-registration-data/clinical-data/bloodGroup";
import { Status } from "lib/build/forms/ktx/committeeOpinion";
import { statusByNephrologistMessages } from "lib/build/forms/ktx/patientCondition";

import { computeAge } from "src/common/hooks/useFormatAge";
import { useFormatMessageMap } from "src/common/hooks/useFormatMessageMap";
import { useFormatName } from "src/common/hooks/useFormatName";
import {
	getEllapsedTimeInMonths,
	getNumberOfDays
} from "src/common/utils/dates";
import { useKnownUsers } from "src/features/auth";
import { orgMessages, sexMessages } from "src/messages/generic";
import { WaitingListTime } from "src/modules/kd/patient-list/types";
import {
	longStatusMessages,
	shortStatusMessages
} from "src/modules/ktx/messages";
import { queries } from "src/modules/ktx/patient-list/queries";
import { Patient } from "src/modules/ktx/patient-list/types";
import { findEvent, RawPatient } from "src/modules/shared/patient-list";

const formatDateOpts = {
	year: "numeric",
	month: "numeric",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	second: "numeric"
} as const;

export function useNormalizePatient(): (patient: RawPatient) => Patient {
	const formatMessage = useFormatMessageMap();
	const formatName = useFormatName();
	const users = useKnownUsers();
	const { formatDate } = useIntl();

	return React.useCallback(
		(rawPatient: RawPatient): Patient => {
			const {
				id,
				socialSecurityNumber,
				orgInfos,
				title = "",
				firstName,
				lastName,
				sex,
				dateOfBirth,
				modifTime,
				extra
			} = rawPatient;

			const statusEvent = findEvent<string>(extra, queries.status);
			const addressEvent = findEvent<{ city: string }>(
				extra,
				queries.address
			);
			const weightEvent = findEvent<number>(extra, queries.weight);
			const ab0Event = findEvent<string>(extra, queries.ab0);
			const rhEvent = findEvent<string>(extra, queries.rh);
			const waitingListEvent = findEvent<Date>(
				extra,
				queries.waitingListStartDate
			);
			const assignedUserEvent = findEvent<string>(
				extra,
				queries.assignedUser
			);
			const statusByNephrologistEvent = findEvent<string>(
				extra,
				queries.statusByNephrologist
			);

			const { value: statusValue = Status.NEW } = statusEvent || {};
			const status = {
				value: statusValue,
				shortLabel: formatMessage(shortStatusMessages, statusValue),
				longLabel: formatMessage(longStatusMessages, statusValue)
			};

			const name = formatName({
				title,
				firstName,
				lastName
			});

			let bloodGroup = "";
			if (ab0Event !== null && rhEvent !== null) {
				const ab0Label = formatMessage(ab0Messages, ab0Event.value);
				const rhLabel = formatMessage(rhMessages, rhEvent.value);
				bloodGroup = `${ab0Label} RH ${rhLabel}`;
			}

			let waitingListTime: WaitingListTime | undefined;
			if (waitingListEvent !== null) {
				const now = new Date();
				const waitingListMonths = getEllapsedTimeInMonths(
					new Date(waitingListEvent.value),
					now
				);
				const waitingListDays = getNumberOfDays(
					waitingListEvent.value,
					now
				);

				waitingListTime = {
					days: waitingListDays,
					months: waitingListMonths
				};
			}

			let assignedUser: string | undefined;
			let assignTime: string | undefined;
			if (assignedUserEvent !== null && users[assignedUserEvent.value]) {
				assignedUser = formatName(users[assignedUserEvent.value]);
				assignTime = formatDate(
					assignedUserEvent.timestamp,
					formatDateOpts
				);
			}

			let statusByNephrologist = "";
			if (statusByNephrologistEvent) {
				statusByNephrologist = formatMessage(
					statusByNephrologistMessages,
					statusByNephrologistEvent.value
				);
			}

			const dialysisCentre = orgInfos.find(item => item.dialysisCentre);
			return {
				id,

				status,
				name,
				dialysisCentre: dialysisCentre
					? formatMessage(orgMessages, dialysisCentre.code)
					: "-",
				socialSecurityNumber,

				age: computeAge(dateOfBirth),
				sex: formatMessage(sexMessages, sex),
				city: addressEvent === null ? "" : addressEvent.value.city,
				weight: weightEvent,
				bloodGroup,

				waitingListTime,
				assignedUser: assignedUser,
				assignTime,
				modifTime: formatDate(modifTime, formatDateOpts),
				statusByNephrologist
			};
		},
		[formatMessage, formatName, users, formatDate]
	);
}
